import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api";
import { fmtDate, fmtINR } from "../fmt";

interface NetWorthResponse {
  assets: number;
  loans: number;
  netWorth: number;
  trend: { month: string; assets: number; loans: number; netWorth: number }[];
}

interface Reminder {
  id: string;
  title: string;
  amount: number | null;
  nextDue: string | null;
  active: boolean;
}

interface Transaction {
  id: string;
  date: string;
  amount: number;
  drcr: "dr" | "cr";
  narration: string | null;
}

interface Insight {
  id: string;
  kind: string;
  severity: "info" | "warn" | "critical";
  title: string;
  body: string;
}

function inNext7Days(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + "T00:00:00");
  const diffDays = (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 7;
}

export function Dashboard() {
  const [networth, setNetworth] = useState<NetWorthResponse | null>(null);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [recent, setRecent] = useState<Transaction[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [nw, rem, txns] = await Promise.all([
          api.get<NetWorthResponse>("/api/networth"),
          api.get<Reminder[]>("/api/reminders"),
          api.get<Transaction[]>("/api/transactions"),
        ]);
        if (cancelled) return;
        setNetworth(nw);
        setReminders(rem);
        setRecent(txns.slice(0, 10));
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "failed to load dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Insights load independently: AI may be off (409/empty) and that must never
  // blank the rest of the dashboard.
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ insights: Insight[] }>("/api/ai/insights")
      .then((r) => {
        if (!cancelled) setInsights(r.insights);
      })
      .catch(() => {
        /* AI off / not configured — no insights section */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function dismissInsight(id: string) {
    setInsights((xs) => xs.filter((x) => x.id !== id));
    try {
      await api.post(`/api/ai/insights/${id}/status`, { status: "dismissed" });
    } catch {
      /* best-effort */
    }
  }

  const duesSoon = reminders.filter((r) => r.active && inNext7Days(r.nextDue));

  return (
    <div>
      <h1>Dashboard</h1>
      {error && <div className="error-text">{error}</div>}

      <div className="card-row">
        <div className="card stat-card">
          <div className="label">Net worth</div>
          <div className="value">{networth ? fmtINR(networth.netWorth) : "..."}</div>
        </div>
        <div className="card stat-card">
          <div className="label">Total assets</div>
          <div className="value">{networth ? fmtINR(networth.assets) : "..."}</div>
        </div>
        <div className="card stat-card">
          <div className="label">Total loans outstanding</div>
          <div className="value">{networth ? fmtINR(networth.loans) : "..."}</div>
        </div>
      </div>

      {insights.length > 0 && (
        <>
          <h2>Insights ✨</h2>
          <div className="insight-grid">
            {insights.slice(0, 6).map((it) => (
              <div key={it.id} className={`card insight-card ${it.severity}`}>
                <button
                  type="button"
                  className="insight-dismiss"
                  aria-label="Dismiss"
                  onClick={() => void dismissInsight(it.id)}
                >
                  ×
                </button>
                <div className="insight-title">{it.title}</div>
                <div className="insight-body muted">{it.body}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <h2>Dues in next 7 days</h2>
      <div className="card">
        {duesSoon.length === 0 ? (
          <div className="muted">Nothing due in the next 7 days.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {duesSoon.map((r) => (
              <li key={r.id}>
                {r.title} — {r.amount != null ? fmtINR(r.amount) : "no amount"} due {fmtDate(r.nextDue)}
              </li>
            ))}
          </ul>
        )}
        <div style={{ marginTop: "0.5rem" }}>
          <Link to="/reminders">View all reminders</Link>
        </div>
      </div>

      <h2>Recent transactions</h2>
      <div className="card" style={{ padding: 0 }}>
        {recent.length === 0 ? (
          <div className="empty-state">No transactions yet.</div>
        ) : (
          <div className="table-wrap" style={{ border: "none" }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Narration</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((t) => (
                  <tr key={t.id}>
                    <td>{fmtDate(t.date)}</td>
                    <td>{t.narration ?? "-"}</td>
                    <td className={`num ${t.drcr}`}>
                      {t.drcr === "dr" ? "-" : "+"}
                      {fmtINR(t.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ padding: "0.75rem" }}>
          <Link to="/transactions">View all transactions</Link>
        </div>
      </div>
    </div>
  );
}
